import { useSyncExternalStore } from 'react';
import { VideoPresets, VideoQuality } from 'livekit-client';
import type { VideoPreset } from 'livekit-client';

/**
 * MN-11: what THIS viewer has chosen about video quality, shared between the
 * dock's quality menu (which applies it to the room) and the per-tile stats
 * readout (which only reads `stats`).
 *
 * A module-level store rather than React context, so neither component needs
 * a provider mounted above the stage — Call.tsx only has to place the two of
 * them in CallStage's slots. Remembered per browser, except the per-person
 * overrides: a person on a weak link wants the same caps next call without
 * setting them again.
 */

/** Camera capture resolution: the top layer you SEND. Simulcast layers scale from it. */
export const SEND_RESOLUTIONS = {
  '1080p': VideoPresets.h1080,
  '720p': VideoPresets.h720,
  '540p': VideoPresets.h540,
  '360p': VideoPresets.h360,
} as const satisfies Record<string, VideoPreset>;
export type SendResolution = keyof typeof SEND_RESOLUTIONS;

/** Ceiling on what you RECEIVE (or send, as the simulcast layers allowed out). */
export const LEVELS = {
  high: VideoQuality.HIGH,
  med: VideoQuality.MEDIUM,
  low: VideoQuality.LOW,
} as const;
export type Level = keyof typeof LEVELS;

export interface QualityPrefs {
  sendRes: SendResolution;
  /** Highest simulcast layer published: your upload cap. */
  sendCap: Level;
  /** Receive ceiling for everyone without an override. */
  recv: Level;
  /** Receive ceiling per remote identity, overriding `recv`. This call only:
   *  identities are per device and per join, so persisting them would only
   *  pile up stale keys. */
  recvFor: Record<string, Level>;
  /** Show the stats readout on every tile. */
  stats: boolean;
  /** An operator's caps from the host (MWE-9, `meetnixctl call ROOM tune`),
   *  on top of this person's own choice. This call only, never saved. */
  op: OperatorCaps;
}

export interface OperatorCaps {
  recv?: Level;
  sendCap?: Level;
  by?: string;
}

const KEY = 'meetnix.quality.v1';

const DEFAULTS: QualityPrefs = {
  // LiveKit's own capture default, so choosing nothing changes nothing.
  sendRes: '720p',
  sendCap: 'high',
  recv: 'high',
  recvFor: {},
  stats: false,
  op: {},
};

function load(): QualityPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const p = JSON.parse(raw) as Partial<QualityPrefs>;
    return {
      sendRes: p.sendRes && p.sendRes in SEND_RESOLUTIONS ? p.sendRes : DEFAULTS.sendRes,
      sendCap: p.sendCap && p.sendCap in LEVELS ? p.sendCap : DEFAULTS.sendCap,
      recv: p.recv && p.recv in LEVELS ? p.recv : DEFAULTS.recv,
      recvFor: {},
      stats: p.stats === true,
      op: {},
    };
  } catch {
    return DEFAULTS;
  }
}

let state: QualityPrefs = load();
const listeners = new Set<() => void>();

export function setPrefs(patch: Partial<QualityPrefs>): void {
  state = { ...state, ...patch };
  try {
    const { recvFor: _thisCallOnly, op: _operators, ...remembered } = state;
    localStorage.setItem(KEY, JSON.stringify(remembered));
  } catch {
    // Private window or blocked storage: the choice still holds for this call.
  }
  listeners.forEach((l) => l());
}

export function getPrefs(): QualityPrefs {
  return state;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useQualityPrefs(): QualityPrefs {
  return useSyncExternalStore(subscribe, getPrefs, getPrefs);
}

/** The lower of two layers; an operator's cap never RAISES a choice. */
function lower(a: Level, b: Level | undefined): Level {
  const order: Level[] = ['low', 'med', 'high'];
  return b !== undefined && order.indexOf(b) < order.indexOf(a) ? b : a;
}

/** The receive ceiling that applies to one remote identity. */
export function recvLevelFor(p: QualityPrefs, identity: string): Level {
  return lower(p.recvFor[identity] ?? p.recv, p.op.recv);
}

/** The upload cap in force: this person's, or an operator's if lower. */
export function sendCapFor(p: QualityPrefs): Level {
  return lower(p.sendCap, p.op.sendCap);
}
